import type { FC } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
} from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import { useChangePasswordMutation } from 'src/graphql/idr/user/mutations/changePassword.generated';
import { parseErrorMsg } from 'src/utils/errors';

const ProfileSecuritySettings: FC = (props) => {
  const { mutate: changePassword } = useChangePasswordMutation();
  const { user, initialize } = useAuth();
  return (
    <Formik
      initialValues={{
        oldPassword: '',
        newPassword: '',
        confirmedNewPassword: '',
      }}
      validationSchema={Yup.object().shape({
        oldPassword: user.forceChangePassword
          ? Yup.string()
          : Yup.string().required('رمز عبور قدیمی الزامی می‌باشد'),
        newPassword: Yup.string()
          .matches(
            /^(?=.*[A-Za-z])(?=.*\d)(?=.{8,})/,
            'پسورد باید حداقل ۸ کاراکتر و شامل ۱ عدد و ۱ حرف باشد'
          )
          .required('رمز عبور جدید الزامی می‌باشد'),
        confirmedNewPassword: Yup.string()
          .oneOf(
            [Yup.ref('newPassword'), null],
            'تکرار رمز عبور یکسان نمی‌باشد'
          )
          .required('تکرار رمز عبور الزامی می‌باشد'),
      })}
      onSubmit={(values, { resetForm, setSubmitting }): void => {
        changePassword(
          {
            data: {
              dto: {
                oldPassword: values.oldPassword,
                newPassword: values.newPassword,
                confirmedNewPassword: values.confirmedNewPassword,
              },
            },
          },
          {
            onSuccess: () => {
              resetForm();
              setSubmitting(false);
              toast.success('رمز عبور با موفقیت ویرایش شد');
              initialize();
            },
            onError: (err: any) => {
              toast.error(parseErrorMsg(err));
              setSubmitting(false);
            },
          }
        );
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }): JSX.Element => (
        <form onSubmit={handleSubmit} {...props}>
          <Card>
            <CardHeader title="تغییر رمز‌عبور" />
            <Box sx={{ pt: 3 }} />
            <CardContent>
              <Grid container spacing={3}>
                {!user.forceChangePassword && (
                  <Grid item md={4} sm={6} xs={12}>
                    <TextField
                      error={Boolean(touched.oldPassword && errors.oldPassword)}
                      fullWidth
                      helperText={touched.oldPassword && errors.oldPassword}
                      label="رمز‌عبور قدیمی"
                      name="oldPassword"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="password"
                      value={values.oldPassword}
                      variant="outlined"
                    />
                  </Grid>
                )}
                <Grid item md={user.forceChangePassword ? 6 : 4} sm={6} xs={12}>
                  <TextField
                    error={Boolean(touched.newPassword && errors.newPassword)}
                    fullWidth
                    helperText={touched.newPassword && errors.newPassword}
                    label="رمز‌عبور جدید"
                    name="newPassword"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="password"
                    value={values.newPassword}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={user.forceChangePassword ? 6 : 4} sm={6} xs={12}>
                  <TextField
                    error={Boolean(
                      touched.confirmedNewPassword &&
                        errors.confirmedNewPassword
                    )}
                    fullWidth
                    helperText={
                      touched.confirmedNewPassword &&
                      errors.confirmedNewPassword
                    }
                    label="تکرار رمز‌عبور"
                    name="confirmedNewPassword"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="password"
                    value={values.confirmedNewPassword}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </CardContent>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 2,
              }}
            >
              <Button
                color="primary"
                disabled={isSubmitting}
                type="submit"
                variant="contained"
              >
                تایید
              </Button>
            </Box>
          </Card>
        </form>
      )}
    </Formik>
  );
};

export default ProfileSecuritySettings;
